import { useState, useEffect, useCallback } from 'react';
import {
  BarChart3,
  RefreshCw,
  TrendingUp,
  Users,
  Package,
  Calendar,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  Gavel,
  ArrowUpDown,
} from 'lucide-react';
import { getEventDashboard } from '../../services/eventDashboardApi';

// ─── HELPERS ────────────────────────────────────────────────────────────────

const fmt = (val) =>
  val == null ? '—' : Number(val).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

const fmtNum = (val) =>
  val == null ? '—' : Number(val).toLocaleString('en-US');

const fmtDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const capitalize = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1) : s;

// ─── DATE PRESET HELPERS ────────────────────────────────────────────────────

const getPresetDates = (preset) => {
  const now = new Date();
  switch (preset) {
    case 'this-month': {
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      const end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
      return { start: toISO(start), end: toISO(end) };
    }
    case 'this-year': {
      const start = new Date(now.getFullYear(), 0, 1);
      const end = new Date(now.getFullYear(), 11, 31, 23, 59, 59);
      return { start: toISO(start), end: toISO(end) };
    }
    case 'last-30': {
      const start = new Date(now);
      start.setDate(start.getDate() - 30);
      return { start: toISO(start), end: toISO(now) };
    }
    case 'last-year': {
      const start = new Date(now.getFullYear() - 1, 0, 1);
      const end = new Date(now.getFullYear() - 1, 11, 31, 23, 59, 59);
      return { start: toISO(start), end: toISO(end) };
    }
    default:
      return { start: '', end: '' };
  }
};

const toISO = (d) => d.toISOString().slice(0, 10);

// ─── STAT CARD ───────────────────────────────────────────────────────────────

const StatCard = ({ label, value, icon: Icon, sub }) => (
  <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5 flex items-start gap-4">
    <div className="rounded-lg p-2.5 bg-blue-50 dark:bg-blue-900/30 shrink-0">
      <Icon className="text-blue-600 dark:text-blue-400" size={20} />
    </div>
    <div className="min-w-0">
      <p className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide">{label}</p>
      <p className="text-xl font-bold text-slate-900 dark:text-slate-100 mt-0.5 truncate">{value}</p>
      {sub && <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">{sub}</p>}
    </div>
  </div>
);

// ─── TENANT CARD ─────────────────────────────────────────────────────────────

const TenantCard = ({ name, data }) => {
  const [expanded, setExpanded] = useState(false);
  const hasError = !!data.error;

  return (
    <div className={`bg-white dark:bg-slate-800 rounded-xl border ${hasError ? 'border-red-200 dark:border-red-800' : 'border-slate-200 dark:border-slate-700'} overflow-hidden`}>
      {/* Header */}
      <div className={`px-5 py-4 flex items-center justify-between ${hasError ? 'bg-red-50 dark:bg-red-950/30' : 'bg-slate-50 dark:bg-slate-700/40'}`}>
        <div className="flex items-center gap-2 min-w-0">
          <div className={`w-2 h-2 rounded-full shrink-0 ${hasError ? 'bg-red-400' : 'bg-green-400'}`} />
          <span className="font-semibold text-slate-800 dark:text-slate-200 capitalize truncate">{name}</span>
        </div>
        <div className="flex items-center gap-3 shrink-0 ml-2">
          {!hasError && (
            <span className="text-sm font-bold text-blue-600 dark:text-blue-400">{fmt(data.totalHammer)}</span>
          )}
          <button
            onClick={() => setExpanded((p) => !p)}
            className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
          >
            {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
        </div>
      </div>

      {/* Error state */}
      {hasError && (
        <div className="px-5 py-4 flex items-start gap-2 text-red-600 dark:text-red-400">
          <AlertCircle size={16} className="shrink-0 mt-0.5" />
          <p className="text-sm">{data.error}</p>
        </div>
      )}

      {/* Summary row (always visible when no error) */}
      {!hasError && (
        <div className="px-5 py-3 grid grid-cols-2 sm:grid-cols-4 gap-3 border-b border-slate-100 dark:border-slate-700">
          <div>
            <p className="text-xs text-slate-400 dark:text-slate-500">Events</p>
            <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">{fmtNum(data.totalEvents)}</p>
          </div>
          <div>
            <p className="text-xs text-slate-400 dark:text-slate-500">Total Lots</p>
            <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">{fmtNum(data.totalLots)}</p>
          </div>
          <div>
            <p className="text-xs text-slate-400 dark:text-slate-500">Sold</p>
            <p className="text-sm font-semibold text-green-600 dark:text-green-400">{fmtNum(data.totalSold)}</p>
          </div>
          <div>
            <p className="text-xs text-slate-400 dark:text-slate-500">Attendees</p>
            <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">{fmtNum(data.totalAttendees)}</p>
          </div>
        </div>
      )}

      {/* Expanded detail */}
      {!hasError && expanded && (
        <div className="px-5 py-4 space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-3 text-sm">
            <div>
              <p className="text-xs text-slate-400 dark:text-slate-500">Avg Hammer / Event</p>
              <p className="font-medium text-slate-800 dark:text-slate-200">{fmt(data.avgHammerPerEvent)}</p>
            </div>
            <div>
              <p className="text-xs text-slate-400 dark:text-slate-500">No Sale</p>
              <p className="font-medium text-slate-800 dark:text-slate-200">{fmtNum(data.totalNoSale)}</p>
            </div>
            <div>
              <p className="text-xs text-slate-400 dark:text-slate-500">Buy Back</p>
              <p className="font-medium text-slate-800 dark:text-slate-200">{fmtNum(data.totalBuyBack)}</p>
            </div>
            <div>
              <p className="text-xs text-slate-400 dark:text-slate-500">Pre-bid Lots</p>
              <p className="font-medium text-slate-800 dark:text-slate-200">{fmtNum(data.totalPrebidLots)}</p>
            </div>
            <div>
              <p className="text-xs text-slate-400 dark:text-slate-500">Max-bid Lots</p>
              <p className="font-medium text-slate-800 dark:text-slate-200">{fmtNum(data.totalMaxbidLots)}</p>
            </div>
            <div>
              <p className="text-xs text-slate-400 dark:text-slate-500">Pending Hammer</p>
              <p className="font-medium text-slate-800 dark:text-slate-200">{fmt(data.totalPendingHammer)}</p>
            </div>
          </div>

          {data.topEvents?.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2">Top Events</p>
              <div className="space-y-1.5">
                {data.topEvents.map((ev) => (
                  <div key={ev.id} className="flex items-start justify-between gap-2 text-sm">
                    <div className="min-w-0">
                      <p className="text-slate-700 dark:text-slate-300 truncate font-medium">{ev.eventName || '—'}</p>
                      <p className="text-xs text-slate-400 dark:text-slate-500">{fmtDate(ev.scheduledDateTime)} · {ev.totalLots} lots · {ev.status}</p>
                    </div>
                    <span className="text-blue-600 dark:text-blue-400 font-semibold shrink-0">{fmt(ev.totalHammer)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── COMPARISON TABLE ────────────────────────────────────────────────────────

const SORT_COLS = [
  { key: 'name', label: 'Tenant' },
  { key: 'totalHammer', label: 'Total Hammer' },
  { key: 'totalEvents', label: 'Events' },
  { key: 'totalLots', label: 'Lots' },
  { key: 'totalSold', label: 'Sold' },
  { key: 'totalAttendees', label: 'Attendees' },
  { key: 'avgHammerPerEvent', label: 'Avg / Event' },
];

const ComparisonTable = ({ tenants }) => {
  const [sortKey, setSortKey] = useState('totalHammer');
  const [sortDir, setSortDir] = useState('desc');

  const rows = Object.entries(tenants)
    .filter(([, d]) => !d.error)
    .map(([name, d]) => ({ name, ...d }))
    .sort((a, b) => {
      const av = sortKey === 'name' ? a.name : (a[sortKey] ?? 0);
      const bv = sortKey === 'name' ? b.name : (b[sortKey] ?? 0);
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir('desc'); }
  };

  if (rows.length === 0) return null;

  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
      <div className="px-5 py-3 border-b border-slate-200 dark:border-slate-700">
        <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300">Tenant Comparison</h3>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-100 dark:border-slate-700">
              {SORT_COLS.map((col) => (
                <th
                  key={col.key}
                  onClick={() => toggleSort(col.key)}
                  className="px-4 py-2.5 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide cursor-pointer hover:text-slate-700 dark:hover:text-slate-200 whitespace-nowrap select-none"
                >
                  <span className="flex items-center gap-1">
                    {col.label}
                    <ArrowUpDown size={12} className={sortKey === col.key ? 'text-blue-500' : 'opacity-30'} />
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.name} className="border-b border-slate-50 dark:border-slate-700/50 hover:bg-slate-50 dark:hover:bg-slate-700/30">
                <td className="px-4 py-2.5 font-medium text-slate-800 dark:text-slate-200 capitalize">{row.name}</td>
                <td className="px-4 py-2.5 font-semibold text-blue-600 dark:text-blue-400">{fmt(row.totalHammer)}</td>
                <td className="px-4 py-2.5 text-slate-700 dark:text-slate-300">{fmtNum(row.totalEvents)}</td>
                <td className="px-4 py-2.5 text-slate-700 dark:text-slate-300">{fmtNum(row.totalLots)}</td>
                <td className="px-4 py-2.5 text-green-600 dark:text-green-400 font-medium">{fmtNum(row.totalSold)}</td>
                <td className="px-4 py-2.5 text-slate-700 dark:text-slate-300">{fmtNum(row.totalAttendees)}</td>
                <td className="px-4 py-2.5 text-slate-700 dark:text-slate-300">{fmt(row.avgHammerPerEvent)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────

const PRESETS = [
  { id: 'all-time', label: 'All Time' },
  { id: 'this-year', label: 'This Year' },
  { id: 'last-year', label: 'Last Year' },
  { id: 'this-month', label: 'This Month' },
  { id: 'last-30', label: 'Last 30 Days' },
  { id: 'custom', label: 'Custom' },
];

const EventDashboard = () => {
  const [preset, setPreset] = useState('all-time');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastFetched, setLastFetched] = useState(null);

  const getActiveDates = useCallback(() => {
    if (preset === 'custom') return { start: customStart || null, end: customEnd || null };
    if (preset === 'all-time') return { start: null, end: null };
    return getPresetDates(preset);
  }, [preset, customStart, customEnd]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { start, end } = getActiveDates();
      const result = await getEventDashboard(start, end);
      setData(result);
      setLastFetched(new Date());
    } catch (err) {
      setError(err.message || 'Failed to load dashboard.');
    } finally {
      setLoading(false);
    }
  }, [getActiveDates]);

  useEffect(() => {
    if (preset !== 'custom') load();
  }, [preset]); // eslint-disable-line

  // Cross-tenant totals
  const totals = data
    ? Object.values(data.tenants).reduce(
        (acc, t) => {
          if (t.error) return acc;
          acc.totalHammer += Number(t.totalHammer) || 0;
          acc.totalEvents += t.totalEvents || 0;
          acc.totalLots += t.totalLots || 0;
          acc.totalAttendees += t.totalAttendees || 0;
          acc.totalSold += t.totalSold || 0;
          return acc;
        },
        { totalHammer: 0, totalEvents: 0, totalLots: 0, totalAttendees: 0, totalSold: 0 }
      )
    : null;

  const tenantCount = data ? Object.keys(data.tenants).length : 0;
  const errorCount = data ? Object.values(data.tenants).filter((t) => t.error).length : 0;

  return (
    <div className="space-y-6 p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <BarChart3 className="text-blue-600 dark:text-blue-400" size={22} />
          <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-200">Event Dashboard</h2>
          {lastFetched && (
            <span className="text-xs text-slate-400 dark:text-slate-500 ml-1">
              as of {lastFetched.toLocaleTimeString()}
            </span>
          )}
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-medium transition-colors"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {/* Date filter */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1 mr-2">
            <Calendar size={15} className="text-slate-400" />
            <span className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide">Period</span>
          </div>
          {PRESETS.map((p) => (
            <button
              key={p.id}
              onClick={() => setPreset(p.id)}
              className={`px-3 py-1.5 text-sm rounded-lg font-medium transition-colors ${
                preset === p.id
                  ? 'bg-blue-600 text-white'
                  : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>

        {preset === 'custom' && (
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <label className="text-sm text-slate-500 dark:text-slate-400">From</label>
              <input
                type="date"
                value={customStart}
                onChange={(e) => setCustomStart(e.target.value)}
                className="px-2.5 py-1.5 text-sm rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200"
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm text-slate-500 dark:text-slate-400">To</label>
              <input
                type="date"
                value={customEnd}
                onChange={(e) => setCustomEnd(e.target.value)}
                className="px-2.5 py-1.5 text-sm rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200"
              />
            </div>
            <button
              onClick={load}
              disabled={loading || !customStart}
              className="px-3 py-1.5 text-sm rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-medium"
            >
              Apply
            </button>
          </div>
        )}
      </div>

      {/* Error banner */}
      {error && (
        <div className="flex items-start gap-2 rounded-xl bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-800 px-4 py-3 text-red-700 dark:text-red-400 text-sm">
          <AlertCircle size={16} className="shrink-0 mt-0.5" />
          {error}
        </div>
      )}

      {/* Loading skeleton */}
      {loading && !data && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-24 rounded-xl bg-slate-100 dark:bg-slate-700 animate-pulse" />
          ))}
        </div>
      )}

      {/* Results */}
      {data && (
        <>
          {/* Cross-tenant summary */}
          {totals && (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              <StatCard
                label="Total Hammer"
                value={fmt(totals.totalHammer)}
                icon={Gavel}
                sub={`across ${tenantCount - errorCount} tenant${tenantCount - errorCount !== 1 ? 's' : ''}`}
              />
              <StatCard label="Total Events" value={fmtNum(totals.totalEvents)} icon={Calendar} />
              <StatCard label="Total Lots" value={fmtNum(totals.totalLots)} icon={Package} />
              <StatCard label="Total Sold" value={fmtNum(totals.totalSold)} icon={TrendingUp} />
              <StatCard label="Total Attendees" value={fmtNum(totals.totalAttendees)} icon={Users} />
            </div>
          )}

          {/* Error notice if any tenants failed */}
          {errorCount > 0 && (
            <div className="flex items-center gap-2 text-sm text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 rounded-xl px-4 py-2.5">
              <AlertCircle size={15} />
              {errorCount} tenant{errorCount > 1 ? 's' : ''} failed to load and {errorCount > 1 ? 'are' : 'is'} excluded from the summary.
            </div>
          )}

          {/* Tenant cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {Object.entries(data.tenants).map(([name, tenantData]) => (
              <TenantCard key={name} name={capitalize(name)} data={tenantData} />
            ))}
          </div>

          {/* Comparison table */}
          <ComparisonTable tenants={data.tenants} />

          <p className="text-xs text-slate-400 dark:text-slate-500 text-right">
            Fetched at {new Date(data.fetchedAt).toLocaleString()}
            {data.startDate && ` · from ${fmtDate(data.startDate)}`}
            {data.endDate && ` to ${fmtDate(data.endDate)}`}
          </p>
        </>
      )}
    </div>
  );
};

export default EventDashboard;
